package co.uk.paxes.pinpoint.web

import kotlin.js.Date
import kotlinx.browser.window

/**
 * AdManager handles PlayGama advertisements (Bridge SDK v2).
 * 
 * Features:
 * - Supports both interstitial and rewarded ads via bridge
 * - Rewarded ads: tracks state changes, grants reward only on "rewarded" state
 * - Interstitial ads: simple placement-based display
 * - Enforces minimum delay between ads (60 seconds)
 * - Subscribes to ad lifecycle events
 * - Gracefully skips ads if bridge unavailable (web/web-link/Android)
 * - Handles AUDIO_STATE_CHANGED and PAUSE_STATE_CHANGED for game pause/mute
 * 
 * Platforms:
 * - PlayGama: Shows ads via bridge
 * - Web/Web-link: Ads skipped (no bridge)
 * - Android: Ads skipped (no bridge)
 */
object AdManager {
    
    private var lastAdShownTime: Long = 0
    private const val MIN_AD_DELAY_MS = 30000  // 30 seconds minimum between ads (reduced from 60)
    
    private var isInitialized = false
    private var currentRewardCallback: (() -> Unit)? = null
    
    /**
     * Initialize AdManager and subscribe to ad lifecycle events.
     * Called once during app startup.
     */
    fun initialize() {
        if (isInitialized) {
            console.log("ℹ️ AdManager already initialized")
            return
        }
        
        subscribeToGameStateChanges()
        console.log("✅ AdManager initialized")
        isInitialized = true
    }
    
    /**
     * Subscribe to game pause/mute state changes.
     * Game should pause when ads are shown.
     */
    private fun subscribeToGameStateChanges() {
        try {
            val bridge: dynamic = js("window.bridge")
            
            if (bridge != null && js("typeof bridge.on === 'function'") as Boolean) {
                // Subscribe to audio state changes (muting)
                val audioEventName: dynamic = bridge.EVENT_NAME?.AUDIO_STATE_CHANGED ?: "AUDIO_STATE_CHANGED"
                
                val handleAudioStateChange: dynamic = js("""
                    (function() {
                        return function(state) {
                            var stateStr = typeof state === 'string' ? state : String(state);
                            console.log('🔊 Audio state: ' + stateStr);
                        };
                    })()
                """)
                
                bridge.on(audioEventName, handleAudioStateChange)
                
                // Subscribe to pause state changes
                val pauseEventName: dynamic = bridge.EVENT_NAME?.PAUSE_STATE_CHANGED ?: "PAUSE_STATE_CHANGED"
                
                val handlePauseStateChange: dynamic = js("""
                    (function() {
                        return function(state) {
                            var stateStr = typeof state === 'string' ? state : String(state);
                            console.log('⏸️ Pause state: ' + stateStr);
                        };
                    })()
                """)
                
                bridge.on(pauseEventName, handlePauseStateChange)
                console.log("✅ Subscribed to game state changes (audio/pause)")
            }
        } catch (e: Exception) {
            console.warn("⚠️ Failed to subscribe to game state changes: ${e.message}")
        }
    }
    
    /**
     * Show an interstitial ad at the specified placement.
     * 
     * Only works on PlayGama. On other platforms, returns false without errors.
     * 
     * @param placement Ad placement identifier (e.g., "game_over", "level_completed", "round_milestone")
     * @return true if ad was triggered, false if skipped (throttle, unsupported, or bridge unavailable)
     */
    fun showInterstitialAd(placement: String): Boolean {
        try {
            // Check if ads are supported on this platform
            if (!isBridgeAdvertisementAvailable()) {
                console.log("ℹ️ PlayGama ads not available (web/web-link/Android mode)")
                return false
            }
            
            // Get bridge
            val bridge: dynamic = js("window.bridge")
            if (bridge == null) {
                console.warn("⚠️ bridge is null")
                return false
            }
            
            console.log("📺 Attempting to show interstitial ad via bridge: $placement")
            
            // Use bridge.advertisement.showInterstitial() for interstitial ads
            val advertisement = bridge?.advertisement
            if (advertisement != null && js("typeof advertisement.showInterstitial === 'function'") as Boolean) {
                console.log("📺 Calling bridge.advertisement.showInterstitial('$placement')")
                
                try {
                    val promise: dynamic = advertisement.showInterstitial(placement)
                    if (promise != null && js("typeof promise.then === 'function'") as Boolean) {
                        promise.then(
                            { console.log("✅ Interstitial ad completed: $placement") },
                            { err: dynamic -> console.warn("⚠️ Interstitial ad failed: $err") }
                        )
                    }
                } catch (e: Exception) {
                    console.warn("⚠️ Exception calling showInterstitial: ${e.message}")
                }
                return true
            }
            
            console.warn("⚠️ bridge.advertisement.showInterstitial not available")
            return false
            
        } catch (e: Exception) {
            console.warn("⚠️ Error showing interstitial ad: ${e.message}")
            return false
        }
    }
    
    /**
     * Show a rewarded video ad via bridge.advertisement.showRewarded()
     * 
     * The reward callback is called when state becomes "rewarded", not when ad closes.
     */
    fun showRewardedVideoAd(placement: String, onReward: () -> Unit): Boolean {
        try {
            // Check if ads are supported on this platform
            if (!isBridgeAdvertisementAvailable()) {
                console.log("ℹ️ PlayGama rewarded ads not available (web/web-link/Android mode)")
                return false
            }
            
            // Check if rewarded ads are supported
            val bridge: dynamic = js("window.bridge")
            val advertisement = bridge?.advertisement
            
            if (advertisement != null && js("typeof advertisement.isRewardedSupported === 'function'") as Boolean) {
                if (!(advertisement.isRewardedSupported() as Boolean)) {
                    console.warn("⚠️ Rewarded ads not supported on this platform")
                    return false
                }
            }
            
            // Check throttle: don't show ads more frequently than 60 seconds
            val now = Date().getTime().toLong()
            val timeSinceLastAd = now - lastAdShownTime
            if (timeSinceLastAd < MIN_AD_DELAY_MS) {
                val waitSeconds = (MIN_AD_DELAY_MS - timeSinceLastAd) / 1000
                console.log("⏳ Rewarded ad throttled - wait ${waitSeconds}s until next ad (placement: $placement)")
                return false
            }
            
            console.log("🎬 Showing rewarded video ad via bridge: $placement")
            
            // Store callback in window scope with unique key to avoid conflicts
            val callbackKey = "adManagerRewardCallback_${Date.now()}"
            window.asDynamic()[callbackKey] = onReward
            
            console.log("🎬 Stored reward callback at window.$callbackKey")
            
            // Call showRewarded with placement
            lastAdShownTime = now
            
            if (advertisement != null && js("typeof advertisement.showRewarded === 'function'") as Boolean) {
                console.log("🎬 Calling bridge.advertisement.showRewarded('$placement')")
                
                // Subscribe to rewarded state BEFORE showing ad
                val eventName: dynamic = bridge.EVENT_NAME?.REWARDED_STATE_CHANGED ?: "REWARDED_STATE_CHANGED"
                
                // Create a more robust handler using direct JS to ensure proper closure
                val handleState: dynamic = js("""
                    (function(callback) {
                        return function(state) {
                            var stateStr = typeof state === 'string' ? state : String(state);
                            console.log('🎬 Rewarded state changed: ' + stateStr);
                            if (stateStr === 'rewarded') {
                                console.log('✅ State is rewarded - invoking reward callback');
                                if (typeof callback === 'function') {
                                    try {
                                        console.log('🎬 About to call callback');
                                        callback();
                                        console.log('✅ Callback executed successfully');
                                    } catch(e) {
                                        console.error('❌ Callback error: ' + e);
                                    }
                                } else {
                                    console.warn('⚠️ Callback is not a function: ' + typeof callback);
                                }
                            } else if (stateStr === 'closed') {
                                console.log('📺 Ad closed (not rewarded)');
                            } else if (stateStr === 'failed') {
                                console.warn('⚠️ Ad failed to display');
                            } else if (stateStr === 'opened') {
                                console.log('📺 Ad opened');
                            } else if (stateStr === 'loading') {
                                console.log('⏳ Ad loading');
                            }
                        };
                    })
                """)
                
                // Pass the actual callback function directly (not via window key)
                val stateHandler = handleState(onReward)
                console.log("🎬 Subscribing to REWARDED_STATE_CHANGED event")
                
                try {
                    advertisement.on(eventName, stateHandler)
                    console.log("✅ Successfully subscribed to REWARDED_STATE_CHANGED")
                } catch (e: Exception) {
                    console.warn("⚠️ Failed to subscribe to REWARDED_STATE_CHANGED: ${e.message}")
                }
                
                try {
                    console.log("🎬 Calling advertisement.showRewarded('$placement')")
                    advertisement.showRewarded(placement)
                    console.log("✅ showRewarded() returned successfully")
                } catch (e: Exception) {
                    console.warn("⚠️ Exception calling showRewarded: ${e.message}")
                    window.asDynamic()[callbackKey] = js("undefined")
                    return false
                }
                return true
            }
            
            console.warn("⚠️ bridge.advertisement.showRewarded not available")
            window.asDynamic()[callbackKey] = js("undefined")
            return false
            
        } catch (e: Exception) {
            console.warn("⚠️ Error showing rewarded video ad: ${e.message}")
            return false
        }
    }
    
    /**
     * Subscribe to ad lifecycle events from the bridge.
     * Events: opened, closed, failed
     */
    private fun subscribeToAdLifecycleEvents() {
        try {
            val bridge: dynamic = js("window.bridge")
            val advertisement = bridge?.advertisement
            
            // Check if the bridge supports event subscription
            if (advertisement != null && js("typeof advertisement.on === 'function'") as Boolean) {
                
                // Subscribe to interstitial state changes
                try {
                    // Try EVENT_NAME.INTERSTITIAL_STATE_CHANGED constant
                    val eventName: dynamic = try {
                        bridge.EVENT_NAME?.INTERSTITIAL_STATE_CHANGED ?: "INTERSTITIAL_STATE_CHANGED"
                    } catch (e: Exception) {
                        "INTERSTITIAL_STATE_CHANGED"
                    }
                    
                    // Create callback function in JavaScript
                    val handleAdStateChange: dynamic = js("""
                        (function() {
                            return function(state) {
                                var stateStr = typeof state === 'string' ? state : String(state);
                                switch(stateStr) {
                                    case 'opened':
                                        console.log('📺 Ad opened - game should pause');
                                        break;
                                    case 'closed':
                                        console.log('📺 Ad closed - game should resume');
                                        break;
                                    case 'failed':
                                        console.warn('⚠️ Ad failed to display');
                                        break;
                                    default:
                                        console.log('📺 Ad state changed: ' + stateStr);
                                }
                            };
                        })()
                    """)
                    
                    advertisement.on(eventName, handleAdStateChange)
                    console.log("✅ Subscribed to ad lifecycle events")
                } catch (e: Exception) {
                    console.warn("⚠️ Failed to subscribe to INTERSTITIAL_STATE_CHANGED: ${e.message}")
                }
            } else if (advertisement == null) {
                console.log("ℹ️ bridge.advertisement not available")
            } else {
                console.log("ℹ️ bridge.advertisement.on not available")
            }
        } catch (e: Exception) {
            console.warn("⚠️ Failed to subscribe to ad events: ${e.message}")
        }
    }
    
    /**
     * Check if bridge advertisement API is available.
     * This indicates we're on PlayGama platform.
     */
    private fun isBridgeAdvertisementAvailable(): Boolean {
        return try {
            js("typeof window.bridge !== 'undefined' && typeof window.bridge.advertisement !== 'undefined'") as Boolean
        } catch (e: Exception) {
            false
        }
    }
    
    /**
     * Check if ads are supported on the current platform.
     * Returns true only on PlayGama.
     */
    fun isAdsSupportedOnThisPlatform(): Boolean {
        return isBridgeAdvertisementAvailable()
    }
    
    /**
     * Get the time (in seconds) until the next ad can be shown.
     * Returns 0 if an ad can be shown immediately.
     */
    fun getSecondsSinceLastAd(): Long {
        val now = Date().getTime().toLong()
        val elapsed = now - lastAdShownTime
        return if (elapsed > MIN_AD_DELAY_MS) 0 else (MIN_AD_DELAY_MS - elapsed) / 1000
    }
    
    /**
     * Check if an ad can be shown right now (respecting throttle).
     */
    fun canShowAdNow(): Boolean {
        return getSecondsSinceLastAd() == 0L
    }
    
    /**
     * Reset the ad throttle timer (for testing purposes).
     * Use with caution - only for debugging.
     */
    fun resetAdThrottle() {
        lastAdShownTime = 0
        console.log("⚠️ Ad throttle reset (debug mode)")
    }
    
    /**
     * Get stats about ad showing.
     */
    fun getAdStats(): String {
        val platform = if (isAdsSupportedOnThisPlatform()) "PlayGama" else "Web/Android"
        val secondsSinceLast = getSecondsSinceLastAd()
        val canShowNow = canShowAdNow()
        return "Platform: $platform | Last ad: ${if (lastAdShownTime == 0L) "never" else "${secondsSinceLast}s ago"} | Can show: $canShowNow"
    }
}
